import {ExecutionStatus} from '@store/operations/executions';
import {Pagination} from '@store/common/pagination.model';
import I18n from '@utils/i18n.utils';
import {ServiceKind} from '@store/settings/serviceKinds';
import {TableFilter, TableFilterType} from '@store/common/tableFilter.model';
import {InvoiceClient, InvoiceStatus, InvoiceSupplier} from '@store/invoices/invoices';
import {InvoicingSource} from '@store/common/organisation.model';
import {PaymentTerm, TaxRule} from '@store/common/organisation.enums';
import {ServiceValidationStatusEnum} from '@store/clients/services';

export enum ExecutionLineState {
  ALL = 'ALL',
  FA_OR_RA_REQUIRED = 'FA_OR_RA_REQUIRED',
  DONE = 'DONE',
  RA_REQUIRED = 'RA_REQUIRED',
  FA_REQUIRED = 'FA_REQUIRED',
  FA_DONE = 'FA_DONE',
  RA_DONE = 'RA_DONE',
  RA_DONE_FA_REQUIRED = 'RA_DONE_FA_REQUIRED',
  FA_DONE_RA_REQUIRED = 'FA_DONE_RA_REQUIRED',
  RA_DONE_FA_NOT_REQUIRED = 'RA_DONE_FA_NOT_REQUIRED',
  FA_DONE_RA_NOT_REQUIRED = 'FA_DONE_RA_NOT_REQUIRED',
  FA_PROVISION = 'FA_PROVISION',
  RA_PROVISION = 'RA_PROVISION',
  BP_NOT_NULL_SUPPLIER_NOT_REQUIRED = 'BP_NOT_NULL_SUPPLIER_NOT_REQUIRED',
  SP_NOT_NULL_CLIENT_NOT_REQUIRED = 'SP_NOT_NULL_CLIENT_NOT_REQUIRED',
  NEGATIVE_MARGIN = 'NEGATIVE_MARGIN',
}

export namespace ExecutionLineState {
  export const selectItems = () => [
    ExecutionLineState.ALL,
    ExecutionLineState.FA_OR_RA_REQUIRED,
    ExecutionLineState.DONE,
    ExecutionLineState.RA_REQUIRED,
    ExecutionLineState.FA_REQUIRED,
    ExecutionLineState.RA_DONE,
    ExecutionLineState.FA_DONE,
    ExecutionLineState.RA_DONE_FA_REQUIRED,
    ExecutionLineState.FA_DONE_RA_REQUIRED,
    ExecutionLineState.RA_DONE_FA_NOT_REQUIRED,
    ExecutionLineState.FA_DONE_RA_NOT_REQUIRED,
    ExecutionLineState.RA_PROVISION,
    ExecutionLineState.FA_PROVISION,
    ExecutionLineState.BP_NOT_NULL_SUPPLIER_NOT_REQUIRED,
    ExecutionLineState.SP_NOT_NULL_CLIENT_NOT_REQUIRED,
    ExecutionLineState.NEGATIVE_MARGIN,
  ].map((it) => ({label: I18n.t(`global.executionLineState.${it}`), value: it}));
}

export enum ExecutionLineStatus {
  CREATED = 'CREATED',
  READY_FOR_INVOICING = 'READY_FOR_INVOICING',
  INVOICED_BY_SUPPLIER = 'INVOICED_BY_SUPPLIER',
}

export namespace ExecutionLineStatus {
  export const selectItems = () =>
    [
      ExecutionLineStatus.CREATED,
      ExecutionLineStatus.READY_FOR_INVOICING,
      ExecutionLineStatus.INVOICED_BY_SUPPLIER,
    ].map((it) => ({label: I18n.t(`global.executionLineStatus.${it}`), value: it}));
}

export interface ExecutionLinesGroupedAction {
  executedAt?: Date;
  quantity1?: number;
  quantity2?: number;
  unitCost?: number;
  unitCostInCents?: number;
  unitPrice?: number;
  unitPriceInCents?: number;
  status?: ExecutionLineStatus;
  supplierInvoicingDelayed?: boolean;
  clientInvoicingDelayed?: boolean;
}

export interface InvoiceLineUpdateDto {
  unitCostInCents?: number;
  unitPriceInCents?: number;
  quantity?: number;
  name?: string;
}

export interface ExecutionLineUpdateDto {
  unitCostInCents?: number;
  unitPriceInCents?: number;
  quantity?: number;
  status?: ExecutionLineStatus;
  supplierInvoicingRequired?: boolean;
  clientInvoicingRequired?: boolean;
  supplierInvoicingDelayed?: boolean;
  clientInvoicingDelayed?: boolean;
}

export interface ExecutionLineTotalDto {
  totalCost: number;
  totalPrice: number;
  totalCostVatIncluded: number;
  totalPriceVatIncluded: number;
  isFromSameSupplierUnit: boolean;
  paymentTerm?: PaymentTerm;
  taxRule?: TaxRule;
}

export type ExecutionLinesFilters = {
  filterReadyForInvoicing?: boolean;
  search: string;
  from?: Date;
  to?: Date;
  status?: ExecutionLineStatus;
  filterInvoicingRequired?: boolean;
  tableFilters: TableFilter[];
  excludeTableFilters: TableFilter[];
  invoicingSource?: InvoicingSource;
  state: ExecutionLineState;
  ids?: number[];
  serviceValidationStatus?: ServiceValidationStatusEnum;
};

export namespace ExecutionLinesFilters {
  export const initTableFilters = () =>
    TableFilterType.initTableFilters([
      TableFilterType.CLIENT_ORGANISATION,
      TableFilterType.CLIENT_ORGANISATION_UNIT,
      TableFilterType.SUPPLIER_ORGANISATION,
      TableFilterType.SUPPLIER_ORGANISATION_UNIT,
      TableFilterType.ORGANISATION_TYPOLOGY,
      TableFilterType.WASTE_FAMILY,
      TableFilterType.WASTE,
      TableFilterType.CONTAINER,
      TableFilterType.SERVICE_KIND,
      TableFilterType.SERVICE_KIND_TYPE,
    ]);

  export const initExcludeTableFilters = () =>
    TableFilterType.initTableFilters([
      TableFilterType.EXCLUDE_CLIENT_ORGANISATION,
      TableFilterType.EXCLUDE_CLIENT_ORGANISATION_UNIT,
      TableFilterType.EXCLUDE_SUPPLIER_ORGANISATION,
      TableFilterType.EXCLUDE_SUPPLIER_ORGANISATION_UNIT,
      TableFilterType.EXCLUDE_WASTE_FAMILY,
      TableFilterType.EXCLUDE_WASTE,
      TableFilterType.EXCLUDE_CONTAINER,
      TableFilterType.EXCLUDE_SERVICE_KIND,
      TableFilterType.EXCLUDE_SERVICE_KIND_TYPE,
    ]);
}

export interface ExecutionLineUI {
  id: number;
  executionId: number;
  selected: boolean;
}

export interface ExecutionLinePagination extends Pagination {
  data: ExecutionLine[];
}

export interface ExecutionLine {
  id: number;
  container: string;
  plannedFrom?: Date;
  name: string;
  executedAt?: Date;
  kind: string;
  kindId?: number;
  waste: string;
  quantity: number;
  unit?: string;
  puBuy: number;
  puSell: number;
  totalBuy: number;
  totalSell: number;
  status: ExecutionStatus;
  commentsCount: number;
  issuesCount: number;
  executionLineStatus: ExecutionLineStatus;
  executionId: number;
  supplierInvoicingRequired: boolean;
  clientInvoicingRequired: boolean;
  clientInvoiceLineId: number;
  supplierInvoiceLineId: number;
  supplierInvoicingDelayed: boolean;
  clientInvoicingDelayed: boolean;
  clientInvoiceId: number;
  supplierInvoiceId: number;
  invoiceStatus?: InvoiceStatus;
  additionalInformation?: string;
  clientOrganisationUnit: InvoiceClient;
  supplierOrganisationUnit: InvoiceSupplier;
  cancelledByLineId?: number;
  wasteId?: number;
  clientOrganisationUnitName?: string;
  serviceValidationStatus?: ServiceValidationStatusEnum;

  // ORPHAN
  isNew: boolean;
  isOrphan: boolean;
  serviceKind?: ServiceKind;
  readyForInvoicing: boolean;
}
